import React, {useEffect, useMemo, useState} from 'react';
import {AccessibilityInfo, View} from 'react-native';
import {Calendar} from 'react-native-calendars';
import {useSelector} from 'react-redux';

import Icon from '/component/base/Icon';
import LoadingIndicator from '/component/base/LoadingIndicator';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity/TouchableOpacity';
import {EVENT_DATA, EVENT_NAMES, SCREEN_NAMES} from '/enum/tealium.enum';
import {variablesEdge30Text} from '/redux/selector/remoteConfig.selector';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

import {EdgeChallengeDateParams} from '../Edge365MainScreen/Edge365MainScreen';
import {getTodayDetails} from '../Edge365MainScreen/Edge365MainScreen.util';

import {styles} from './Edge365CalendarScreen.style';

interface Edge365_CalendarProps {
  onDateSelect: (date: string) => void;
  defaultDate: string;
  edge365ChallengeTasks: Array<any>;
  getEdge365ChallengeTasks: (params: EdgeChallengeDateParams) => void;
  coachId: string;
  lastPaidRank: string;
  highestAchievedRank: string;
  coachCurrentRank: string;
}

export const Edge365CalendarScreen: React.FC<Edge365_CalendarProps> = ({
  onDateSelect,
  defaultDate,
  edge365ChallengeTasks,
  getEdge365ChallengeTasks,
  coachId,
  lastPaidRank,
  highestAchievedRank,
  coachCurrentRank,
}) => {
  const todayDetails = getTodayDetails();
  const todayStr = todayDetails.date;
  const [todayYear, todayMonth, todayDay] = todayStr.split('-');
  const todayDate = new Date(
    Number(todayYear),
    Number(todayMonth) - 1,
    Number(todayDay),
  );

  const [selectedDate, setSelectedDate] = useState(defaultDate || todayStr);
  const [visibleMonth, setVisibleMonth] = useState({
    month: todayDetails.month,
    year: todayDetails.year,
  });
  const [isCalendarLoading, setIsCalendarLoading] = useState(false);

  const edge30Details = useSelector(variablesEdge30Text);
  const edge30MinDate = edge30Details?.minDate || '2025-01-01';
  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.CTA_Click,
  });
  const coachIdStr = coachId.toString();

  let minDateObj;
  if (edge30MinDate) {
    const [year, month, day] = edge30MinDate.split('-').map(Number);
    minDateObj = new Date(year, month - 1, day); // Month is 0-indexed
  } else {
    const now = new Date();
    minDateObj = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  useEffect(() => {
    if (defaultDate) {
      const [yearStr, monthStr] = defaultDate.split('-');
      const year = Number(yearStr);
      const month = Number(monthStr);
      setVisibleMonth({month, year});
      setSelectedDate(defaultDate);
      onDateSelect(defaultDate);
    }
  }, [defaultDate]);

  useEffect(() => {
    const loadTasks = async () => {
      setIsCalendarLoading(true);
      try {
        await getEdge365ChallengeTasks({
          coachId,
          month: visibleMonth.month,
          year: visibleMonth.year,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching tasks:', err);
      } finally {
        setIsCalendarLoading(false);
      }
    };

    loadTasks();
  }, [visibleMonth.month, visibleMonth.year]);

  const completedDates = useMemo(
    () =>
      edge365ChallengeTasks
        ?.filter(item => item.isChallengeCompleted)
        .map(item => item.date) || [],
    [edge365ChallengeTasks],
  );

  const markedDates = {
    ...completedDates.reduce((acc, date) => {
      acc[date] = {
        customStyles: {
          container: styles.markedDatesContainer,
          text: styles.markedDatesText,
        },
      };
      return acc;
    }, {} as Record<string, any>),
    ...(selectedDate && {
      [selectedDate]: {
        customStyles: {
          container: styles.todayContainer,
          text: {color: '#fff'},
        },
      },
    }),
  };

  const goToToday = async () => {
    const {month, year, date} = todayDetails;
    const isDifferentMonth =
      visibleMonth.month !== month || visibleMonth.year !== year;
    setVisibleMonth({month, year});
    if (isDifferentMonth) {
      await getEdge365ChallengeTasks({
        coachId,
        month,
        year,
      });
    }
    setSelectedDate(date);
    onDateSelect(date);

    sendEvent({
      screenName: SCREEN_NAMES.edge30,
      CTAtext: EVENT_DATA.CTAtextEdge30,
      CTAtype: EVENT_DATA.CTAtype,
      lastPaidRank: lastPaidRank,
      coachHighestRank: highestAchievedRank,
      coachCurrentRank: coachCurrentRank,
      coachID: coachIdStr,
    });
  };

  return (
    <View style={styles.container}>
      <Calendar
        accessible={true}
        onAccessibilityFocus={() => {
          AccessibilityInfo.announceForAccessibility(
            `${visibleMonth.month}-${visibleMonth.year}`,
          );
        }}
        accessibilityLabel={`${visibleMonth.month}-${visibleMonth.year}`}
        current={`${visibleMonth.year}-${String(visibleMonth.month).padStart(
          2,
          '0',
        )}-01`}
        key={`${visibleMonth.year}-${visibleMonth.month}`}
        style={{backgroundColor: 'transparent'}}
        markedDates={markedDates}
        markingType="custom"
        hideExtraDays={true}
        onVisibleMonthsChange={months => {
          const firstMonth = months[0];
          setVisibleMonth({month: firstMonth.month, year: firstMonth.year});
        }}
        onDayPress={day => {
          setSelectedDate(day.dateString);
          setVisibleMonth({
            year: Number(day.dateString.split('-')[0]),
            month: Number(day.dateString.split('-')[1]),
          });
        }}
        disableArrowLeft={
          visibleMonth &&
          (visibleMonth.year < minDateObj.getFullYear() ||
            (visibleMonth.year === minDateObj.getFullYear() &&
              visibleMonth.month - 1 <= minDateObj.getMonth()))
        }
        disableArrowRight={
          visibleMonth &&
          (visibleMonth.year > todayDate.getFullYear() ||
            (visibleMonth.year === todayDate.getFullYear() &&
              visibleMonth.month - 1 >= todayDate.getMonth()))
        }
        renderHeader={() => {
          const headerDate = new Date(
            visibleMonth.year,
            visibleMonth.month - 1,
            1,
          );
          const formattedMonthYear = headerDate.toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
          });
          return (
            <View
              style={{paddingBottom: 0}}
              accessible={true}
              accessibilityLabel={formattedMonthYear}>
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                style={styles.calendarHeaderText}
                accessible={true}
                accessibilityLabel={formattedMonthYear}>
                {formattedMonthYear}
              </Text>
            </View>
          );
        }}
        renderArrow={direction => {
          const currentDate = new Date(
            visibleMonth.year,
            visibleMonth.month - 1,
            1,
          );

          const isLeftDisabled =
            currentDate.getFullYear() < minDateObj.getFullYear() ||
            (currentDate.getFullYear() === minDateObj.getFullYear() &&
              currentDate.getMonth() === minDateObj.getMonth());

          const isRightDisabled =
            currentDate.getFullYear() > todayDate.getFullYear() ||
            (currentDate.getFullYear() === todayDate.getFullYear() &&
              currentDate.getMonth() >= todayDate.getMonth());

          const shouldHide =
            (direction === 'left' && isLeftDisabled) ||
            (direction === 'right' && isRightDisabled);

          return shouldHide ? (
            <View style={styles.arrowPlaceholder} accessible={true} />
          ) : (
            <Icon
              accessible={true}
              name={direction === 'left' ? 'chevron-left' : 'chevron-right'}
              size={24}
            />
          );
        }}
        onMonthChange={month => {
          const newDate = new Date(month.year, month.month - 1, 1);
          if (newDate < minDateObj || newDate > todayDate) {
            // Prevent month change — ignore or reset state
            return;
          }
          // Allow month change
          setVisibleMonth(month);
        }}
        dayComponent={({date, state}) => {
          const dateStr = date.dateString?.split('T')[0];
          if (!dateStr) return null;

          const [year, month, day] = dateStr.split('-');
          const thisDate = new Date(
            Number(year),
            Number(month) - 1,
            Number(day),
          ); // ensures local midnight
          const isCompleted = completedDates?.includes(dateStr);
          const isFuture = dateStr > todayStr;
          const isBeforeMin = dateStr < edge30MinDate;
          const isOtherMonth = state === 'disabled';
          const isPastOrToday = thisDate <= todayDate;
          const isNotCompleted = isPastOrToday && !isCompleted;
          const isDisabled = isFuture || isBeforeMin;
          const isSelected = selectedDate === dateStr;

          const textStyle = {
            ...styles.dayText,
            color: isSelected ? '#ffffff' : isCompleted ? '#2e7d32' : '#000',
            fontWeight: isCompleted ? 'bold' : 'normal',
            opacity: isOtherMonth ? 0.4 : isDisabled ? 0.3 : 1,
          };

          return (
            <TouchableOpacity
              disabled={isDisabled}
              onPress={() => {
                onDateSelect(dateStr);
                setSelectedDate(dateStr);
              }}
              accessible={true}
              accessibilityLabel={`Day ${date.day}${
                isCompleted ? ', completed' : ''
              }${isNotCompleted && !isDisabled ? ', not completed' : ''}`}
              accessibilityState={{
                disabled: isDisabled,
                selected: isSelected,
              }}>
              <View
                style={[
                  styles.calendarView,
                  isCompleted && styles.calendarViewCompleted,
                  isNotCompleted && !isSelected && styles.notCompletedContainer,
                  isOtherMonth && styles.prevNextMonthDate,
                  isSelected && styles.todayContainer,
                  isDisabled && styles.futureDateContainer,
                ]}
                accessible={true}>
                <View style={styles.cellContainer}>
                  <Text
                    allowFontScaling
                    maxFontSizeMultiplier={2}
                    variant="caption"
                    style={textStyle}>
                    {date.day}
                  </Text>
                  {isCompleted && !isDisabled && !isSelected ? (
                    <Icon name="check" style={styles.checkMark} />
                  ) : isCompleted && isSelected ? (
                    <Icon name="check" style={styles.checkMarkWhite} />
                  ) : isNotCompleted && !isSelected ? (
                    <Icon name="close" style={styles.crossMark} />
                  ) : isNotCompleted && isSelected ? (
                    <Icon name="close" style={styles.crossMarkWhite} />
                  ) : (
                    <Text
                      allowFontScaling
                      maxFontSizeMultiplier={2}
                      style={styles.markPlaceholder}>
                      {' '}
                    </Text>
                  )}
                </View>
              </View>
            </TouchableOpacity>
          );
        }}
      />

      {isCalendarLoading && (
        <View style={styles.calendarLoaderOverlay}>
          <LoadingIndicator size="small" />
        </View>
      )}

      <TouchableOpacity
        onPress={goToToday}
        style={styles.goToTodayContainer}
        accessibilityRole="button"
        accessibilityLabel="Go to today">
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          style={styles.goToTodayText}>
          Go to Today
        </Text>
      </TouchableOpacity>
    </View>
  );
};
